import { ref } from 'vue';

const COPIED_RESET_MS = 2000;

/** Legacy path for insecure contexts and older WebViews, where `navigator.clipboard` is missing or rejects. */
const fallbackCopy = (text: string): boolean => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

/**
 * Copy text to the clipboard, with a `copied` flag that self-clears so callers
 * can show a transient confirmation.
 */
export function useCopyToClipboard(promptMessage = 'Copy:') {
  const copied = ref(false);
  let resetTimer: ReturnType<typeof setTimeout> | undefined;

  const showCopiedState = () => {
    copied.value = true;
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => {
      copied.value = false;
    }, COPIED_RESET_MS);
  };

  const copy = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        showCopiedState();
        return;
      }
    } catch {
      // Fall through to the legacy path below.
    }

    if (fallbackCopy(text)) {
      showCopiedState();
      return;
    }

    // Nothing left but letting the user copy the selection by hand. `prompt`
    // throws in a sandboxed WebView, which would surface as an unhandled rejection.
    try {
      window.prompt(promptMessage, text);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  };

  return { copied, copy };
}
